import { useCallback, useRef, type RefObject } from 'react';

interface DialogFocusReturnOptions {
  /**
   * Where focus goes when the dialog closes and the control that opened it
   * has gone, as a question's Edit button goes while its editor is open.
   */
  fallback?: () => HTMLElement | null | undefined;
  /** The control focused when the dialog opens, in place of its first one. */
  initialFocus?: RefObject<HTMLElement | null>;
}

// The element a closing dialog is giving focus back to, while it does.
let returningFocusTo: HTMLElement | null = null;

/**
 * Whether focus is arriving at `element` because a dialog closed and gave it
 * back. A tooltip stays shut on that focus, as #92 decided: the person did
 * not ask for it.
 */
export function isReturningFocus(element: EventTarget | null) {
  return element !== null && element === returningFocusTo;
}

/**
 * Gives focus back to the control that opened a Radix dialog when it closes.
 * Radix gives it to the dialog's `Dialog.Trigger`, and a dialog opened with
 * none, as most here are, left it on `<body>` (#173). Spread the result on
 * `Dialog.Content`.
 */
export function useDialogFocusReturn({ fallback, initialFocus }: DialogFocusReturnOptions = {}) {
  const openerRef = useRef<HTMLElement | null>(null);

  // The opener still has focus when the dialog opens. `autoFocus` on a field
  // inside moves it first, and stops this running at all: use initialFocus.
  const onOpenAutoFocus = useCallback(
    (event: Event) => {
      const { activeElement, body } = document;
      openerRef.current =
        activeElement instanceof HTMLElement && activeElement !== body ? activeElement : null;

      const first = initialFocus?.current;
      if (first) {
        event.preventDefault();
        first.focus();
      }
    },
    [initialFocus],
  );

  const onCloseAutoFocus = useCallback(
    (event: Event) => {
      event.preventDefault();
      const opener = openerRef.current;
      openerRef.current = null;

      const target = opener?.isConnected ? opener : fallback?.();
      if (!target) {
        return;
      }
      returningFocusTo = target;
      try {
        target.focus();
      } finally {
        returningFocusTo = null;
      }
    },
    [fallback],
  );

  return { onCloseAutoFocus, onOpenAutoFocus };
}
